/**
 * THE TWENTY-FOUR PLATES, OFF THE DISK AND ONTO THE SHEET.
 *
 * -- WHAT WAS MISSING, AND WHY IT WAS MISSING ---------------------------------
 *
 * `chart.ts` has carried a plate box, a painter and a fence for it since the round that
 * measured the room. Nothing ever put a fish in it, because `copy.plate` is supplied by the
 * HOST and the round that baked the twenty-four was forbidden the host file. So the art
 * existed, the box existed, the fence existed, and the page drew paper. This file is the
 * missing half: it turns the baked PNGs into the indexed bitmaps `chart.ts` already knows how
 * to paint, and `src/hud/index.ts` decides which one is under the hand.
 *
 * -- THE PLATES ARE IN THREE TONES, AND ONE OF THEM IS NOT QUITE THE SHEET'S ----
 *
 * `gauntlet/art-technique/fish-plates/plate.py` reduces each render to three tones and a hole.
 * MEASURED over all 24 plates, 2026-09-03: 242,117 opaque pixels in exactly THREE distinct
 * colours and no fourth, which is the property that matters. But one of the three is not the
 * sheet's, and this is a finding rather than a note:
 *
 *   the plates carry       #1d4461   (29, 68, 97)
 *   `CHART_WASH_RGB` is    #1c425e   (28, 66, 94)      distance 3.74
 *   ink and paper          exact, distance 0.00
 *
 * `plate.py` holds `WASH = (29, 68, 97)` under the comment "src/render/palette.ts, read off the
 * running build 2026-09-03". It was mis-copied. Nothing a player can see changes, because this
 * file decodes to a palette INDEX and `chart.ts` then paints that index with the sheet's own
 * colour - the drawn wash is the sheet's wash either way. What it would have broken is an exact
 * lookup, which is what this file first tried, and it went red on the first plate.
 *
 * SO THE DECODE IS NEAREST-OF-THREE WITH A BOUND, NOT A SNAP. The three tones are far apart -
 * INK to WASH is 88.3 and WASH to PAPER is 200.4 in RGB distance - so at a bound of 12 a colour
 * is either unmistakably one of them or it is refused. That tolerates the 3.74 drift already in
 * the tree and still refuses a plate baked against a palette that has genuinely moved, which is
 * the failure worth being loud about: a silent snap is how a drifted plate goes on drawing
 * something that looks nearly right for the rest of the run.
 *
 * -- THE X2 FILES ARE NOT A SECOND FIDELITY AND ARE NOT LOADED -----------------
 *
 * `plate.py --grain 2` writes `<id>-x2.png` as `Image.NEAREST` on the finished plate. MEASURED:
 * `mackerel.png` is 237x60 and `mackerel-x2.png` is 474x120, and every pixel of the second is a
 * 2x2 block of the first. It carries NO detail the 1x does not, it is the grain-fit proof, and
 * the sheet is painted on the 960x540 authoring grid and doubled by the same integer as the
 * sea - so a 474 px plate would not fit the 482 px box at grain 2 either, it would simply be
 * twice as big in art pixels. Loading them would cost 107,283 B for nothing. The glob below is
 * deliberately narrow enough to exclude them, and a fence asserts that it does.
 *
 * -- THEY ARE NEVER ON THE PATH A BLANK PAGE WAITS ON --------------------------
 *
 * `vite.config.ts` sets `assetsInlineLimit` to 12,288 B and the largest plate is 6,189 B, so
 * every one of these would be base64'd into the JS chunk by default: 92,549 B of art, about
 * 123 kB as base64, in front of the first frame, for a picture the player only ever sees after
 * pressing L. `?no-inline` is what stops that, and `tools/instruments/ships-the-game.test.ts`
 * already fences the same property for the islands. On top of that the fetch is LAZY - see
 * `loadPlates`, which the host calls the first time the ledger opens and never at boot - so a
 * player who never opens the ledger never asks for a plate at all.
 */
import { INK, PAPER, WASH } from './chart';
import { bitmap, type Bitmap } from './glyphs';
import { CHART_INK_RGB, CHART_PAPER_RGB, CHART_WASH_RGB } from './palette';
import { SPECIES } from '../sim/index';
import { plateAssetUrls } from './runtime-assets';

/** Every species that must have a plate before any plate is drawn. */
export const PLATE_IDS: readonly string[] = SPECIES.map((s) => s.id);

/**
 * The three tones a plate may carry, against the palette index the sheet paints them with.
 * Built from `palette.ts` rather than written out, so a palette change is measured against the
 * art rather than silently agreed with by a second hand-written copy - which is exactly the
 * mistake `plate.py` made and this file caught.
 */
export const PLATE_TONES: readonly { readonly rgb: readonly [number, number, number]; readonly ink: number }[] = [
  { rgb: CHART_INK_RGB, ink: INK },
  { rgb: CHART_WASH_RGB, ink: WASH },
  { rgb: CHART_PAPER_RGB, ink: PAPER },
];

/**
 * How far a plate's tone may sit from the sheet's before the decode refuses it, in RGB
 * distance. The delivered drift is 3.74 and the nearest two tones are 88.3 apart, so this
 * number has 84 units of room on one side and 8 on the other.
 */
export const TONE_TOLERANCE = 12;

/** The nearest of the three, or -1 if nothing is near enough to be one of them. */
function toneOf(r: number, g: number, b: number): number {
  let best = -1;
  let bestD = Infinity;
  for (const t of PLATE_TONES) {
    const d = Math.hypot(r - t.rgb[0], g - t.rgb[1], b - t.rgb[2]);
    if (d < bestD) {
      bestD = d;
      best = t.ink;
    }
  }
  return bestD <= TONE_TOLERANCE ? best : -1;
}

/**
 * One decoded plate: an indexed bitmap in the sheet's own palette, 0 where the paper shows.
 *
 * ALPHA IS THE ONLY TRANSPARENCY TEST and the threshold is 128, which is the same one
 * `world-view.ts` uses on the baked islands. The plates are hard-edged - `plate.py` writes 0 or
 * 255 and nothing between - so the threshold never actually decides anything on the shipped
 * set, and it is written this way so that a plate with a soft edge would come out with a hard
 * one rather than with a ring of INK at alpha 3.
 */
export function plateFromRgba(w: number, h: number, rgba: Uint8ClampedArray | Uint8Array): Bitmap {
  const b = bitmap(w, h);
  // A plate holds three colours over a quarter of a million pixels, so the lookup is memoised
  // on the packed colour and the distance is computed three times per plate rather than per
  // pixel. It is a cache and nothing else: a hit returns what a cold call would.
  const seen = new Map<number, number>();
  for (let i = 0; i < w * h; i++) {
    if ((rgba[i * 4 + 3] ?? 0) < 128) continue;
    const r = rgba[i * 4] ?? 0;
    const g = rgba[i * 4 + 1] ?? 0;
    const bl = rgba[i * 4 + 2] ?? 0;
    const key = (r << 16) | (g << 8) | bl;
    let ink = seen.get(key);
    if (ink === undefined) {
      ink = toneOf(r, g, bl);
      seen.set(key, ink);
    }
    if (ink < 0) {
      throw new Error(
        `a plate pixel is #${key.toString(16).padStart(6, '0')}, which is not one of the sheet's three tones ` +
          `within ${TONE_TOLERANCE}; the plate was baked against a different palette than the one chart.ts paints with`,
      );
    }
    b.px[i] = ink;
  }
  return b;
}

/**
 * WHETHER THE PLATE IS ON, AND IT IS ALL TWENTY-FOUR OR NONE.
 *
 * The design ledger's hardest rule is that two fidelity tiers of one content class never ship
 * on one surface, and four painted fish beside twenty stubs would repeat the 2026-08-30
 * rejection exactly. `chart.ts` fences the absent case - an absent plate leaves the page
 * byte-identical to what shipped - and this is the switch in front of it. It is a pure function
 * of the map so a test can hold it to the species list rather than to a count, because "24
 * plates" is still true of twenty-four of the wrong ones.
 */
export function platesReady(plates: ReadonlyMap<string, Bitmap>): boolean {
  for (const id of PLATE_IDS) if (!plates.has(id)) return false;
  return true;
}

/**
 * THE URLS, RESOLVED AT BUILD TIME, FETCHED AT RUN TIME.
 *
 * `eager` here costs nothing at run time: with `query: '?no-inline&url'` each entry is a string,
 * so this is 24 strings in the chunk rather than 24 images. The `?no-inline` is load-bearing and
 * is argued at the top of this file.
 */
const plateUrls = plateAssetUrls;

/** The plate ids the build actually carries, in the order the glob resolved them. */
export function bakedPlateIds(): string[] {
  return Object.keys(plateUrls)
    .map((p) => p.slice('./baked/plates/'.length).replace(/\.png$/, ''))
    .sort();
}

/**
 * Every plate the build carries, decoded, or an empty map if any species is missing one.
 *
 * IT RESOLVES EMPTY RATHER THAN THROWING when a fetch fails, for the reason every island load
 * in `world-view.ts` gives: a plate that does not arrive costs a picture, not the page. The
 * caller gets a map that `platesReady` refuses and the sheet is exactly what shipped.
 */
export async function loadPlates(): Promise<ReadonlyMap<string, Bitmap>> {
  const out = new Map<string, Bitmap>();
  const wanted = new Set<string>(PLATE_IDS);
  const jobs: Promise<void>[] = [];
  for (const [path, url] of Object.entries(plateUrls)) {
    const id = path.slice('./baked/plates/'.length).replace(/\.png$/, '');
    if (!wanted.has(id)) continue;
    jobs.push(
      decode(url)
        .then((img) => {
          out.set(id, plateFromRgba(img.w, img.h, img.rgba));
        })
        .catch((e: unknown) => {
          console.warn(`chart-plates: ${id} did not load; the sheet keeps its paper`, e);
        }),
    );
  }
  await Promise.all(jobs);
  return out;
}

async function decode(url: string): Promise<{ w: number; h: number; rgba: Uint8ClampedArray }> {
  const img = new Image();
  img.src = url;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) throw new Error('no 2d context to read a plate through');
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { w: canvas.width, h: canvas.height, rgba: data.data };
}
